// src/design/registry/reservationStateRegistry.ts
// ReservationState.state -> { Icon, color token, labelKey }. Single source of
// truth for the state chip everywhere it appears (Llegadas de hoy, Reservas,
// reservation detail header). Labels come from i18n/{es,en}/reservationStates.ts
// (proposal §3, definitive vocabulary) — never invented inline.
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import PendingIcon from '@mui/icons-material/Pending';
import TodayIcon from '@mui/icons-material/Today';
import DvrIcon from '@mui/icons-material/Dvr';
import SendToMobileIcon from '@mui/icons-material/SendToMobile';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import ErrorIcon from '@mui/icons-material/Error';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import HotelIcon from '@mui/icons-material/Hotel';
import LogoutIcon from '@mui/icons-material/Logout';
import type { ReservationPrimaryState } from '@/types/reservationState';
import type { VisualMeta } from './colors';

const k = (key: string) => `resources.reservationStates.${key}`;

export const RESERVATION_STATE_REGISTRY: Record<ReservationPrimaryState, VisualMeta> = {
  notStarted:           { Icon: RadioButtonUncheckedIcon, color: 'neutral', labelKey: k('notStarted') },
  preCheckInPartial:    { Icon: PendingIcon,               color: 'warning', labelKey: k('preCheckInPartial') },
  preCheckInDone:       { Icon: CheckCircleIcon,           color: 'success', labelKey: k('preCheckInDone') },
  arrivesToday:         { Icon: TodayIcon,                 color: 'info',    labelKey: k('arrivesToday') },
  liveKiosk:            { Icon: DvrIcon,                   color: 'primary', labelKey: k('liveKiosk') },
  liveMobileKiosk:      { Icon: SendToMobileIcon,           color: 'secondary', labelKey: k('liveMobileKiosk') },
  liveAirLobby:         { Icon: PhoneAndroidIcon,           color: 'warning', labelKey: k('liveAirLobby') },
  needsHelp:            { Icon: ErrorIcon,                  color: 'error',   labelKey: k('needsHelp') },
  checkedInKiosk:       { Icon: CheckCircleIcon,            color: 'success', labelKey: k('checkedInKiosk') },
  checkedInMobileKiosk: { Icon: CheckCircleIcon,            color: 'success', labelKey: k('checkedInMobileKiosk') },
  checkedInAirLobby:    { Icon: CheckCircleIcon,            color: 'success', labelKey: k('checkedInAirLobby') },
  keyDelivered:         { Icon: VpnKeyIcon,                 color: 'success', labelKey: k('keyDelivered') },
  kioskLeftMidway:      { Icon: RemoveCircleOutlineIcon,    color: 'neutral', labelKey: k('kioskLeftMidway') },
  noRecord:             { Icon: HelpOutlineIcon,            color: 'neutral', labelKey: k('noRecord') },
  inHouse:              { Icon: HotelIcon,                  color: 'info',    labelKey: k('inHouse') },
  departed:             { Icon: LogoutIcon,                 color: 'neutral', labelKey: k('departed') },
};

export const getReservationStateMeta = (state: string | null | undefined): VisualMeta => {
  const meta = state ? RESERVATION_STATE_REGISTRY[state as ReservationPrimaryState] : undefined;
  return meta ?? { Icon: HelpOutlineIcon, color: 'neutral', labelKey: state ?? '' };
};
